import api from './api'
import {
  LoginCredentials,
  RegisterData,
  AuthResponse,
  User,
  UserRole,
  TherapistLoginResponse,
  OtpVerificationResponse,
  OtpVerificationRequest,
  OtpVerificationSuccessResponse,
  ChangePasswordData,
  AgencyLoginResponse,
} from '../types/index'

export class AuthService {
  private static readonly endpoints = {
    login: '/auth/login',
    register: '/auth/register',
    logout: '/auth/logout',
    refresh: '/auth/refresh',
    profile: '/auth/profile',
    forgotPassword: '/auth/forgot-password',
    resetPassword: '/auth/reset-password',
    changePasswordTherapist: '/therapist/change-password',
    changePasswordPatient: '/patient/change-password',
    changePasswordAgency: '/agency/change-password',
  }

  /**
   * Login user with email and password
   */
  static async login(credentials: LoginCredentials): Promise<AuthResponse> {
    const response = await api.post<AuthResponse>(
      this.endpoints.login,
      credentials
    )
    return response.data
  }

  /**
   * Register new user
   */
  static async register(userData: RegisterData): Promise<AuthResponse> {
    const response = await api.post<AuthResponse>(
      this.endpoints.register,
      userData
    )
    return response.data
  }

  /**
   * Logout current user
   */
  static async logout(): Promise<void> {
    await api.post(this.endpoints.logout)
  }

  /**
   * Refresh authentication token
   */
  static async refreshToken(refreshToken: string): Promise<AuthResponse> {
    const response = await api.post<AuthResponse>(this.endpoints.refresh, {
      refreshToken,
    })
    return response.data
  }

  /**
   * Get current user profile
   */
  static async getProfile(): Promise<User> {
    const response = await api.get<User>(this.endpoints.profile)
    return response.data
  }

  /**
   * Update user profile
   */
  static async updateProfile(userData: Partial<User>): Promise<User> {
    const response = await api.put<User>(this.endpoints.profile, userData)
    return response.data
  }

  /**
   * Send forgot password email
   */
  static async forgotPassword(email: string): Promise<void> {
    await api.post(this.endpoints.forgotPassword, { email })
  }

  /**
   * Reset password with token
   */
  static async resetPassword(
    token: string,
    newPassword: string
  ): Promise<void> {
    await api.post(this.endpoints.resetPassword, {
      token,
      password: newPassword,
    })
  }

  /**
   * Change user password
   */
  static async changePassword(
    role: UserRole,
    data: ChangePasswordData
  ): Promise<any> {
    const endpoint =
      role === UserRole.THERAPIST
        ? this.endpoints.changePasswordTherapist
        : role === UserRole.AGENCY ? this.endpoints.changePasswordAgency : this.endpoints.changePasswordPatient
    const response = await api.put(endpoint, data)
    return response.data
  }

  /**
   * Validate authentication token
   */
  static async validateToken(): Promise<boolean> {
    try {
      await this.getProfile()
      return true
    } catch {
      return false
    }
  }

  /**
   * Therapist-specific login endpoint
   * Returns either AuthResponse (if no OTP required) or OtpVerificationResponse (if OTP required)
   */
  static async therapistLogin(
    credentials: LoginCredentials
  ): Promise<AuthResponse | OtpVerificationResponse> {
    const response = await api.post<
      TherapistLoginResponse | OtpVerificationResponse
    >('/therapist/login', credentials)

    // Check if OTP verification is required
    if (
      'step' in response.data &&
      response.data.step === 'OTP_VERIFICATION_REQUIRED'
    ) {
      return response.data as OtpVerificationResponse
    }

    // Map API response to AuthResponse format (legacy flow without OTP)
    const loginResponse = response.data as TherapistLoginResponse
    const user: User = {
      id: loginResponse.therapist.id,
      email: loginResponse.therapist.email,
      firstName: loginResponse.therapist.firstName,
      lastName: loginResponse.therapist.lastName,
      role: UserRole.THERAPIST,
      avatar: loginResponse.therapist.profilePhotoUrl,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      hasHippaSigned: loginResponse.therapist.hasSignedHIPAA,
      // New provider verification fields
      verificationStatus: loginResponse.therapist.status?.toLowerCase(),
      blockReason: loginResponse.therapist.blockReason,
      providerWorkWith: loginResponse.therapist.providerWorkWith,
    }

    return {
      user,
      token: loginResponse.access_token,
      refreshToken: loginResponse.refresh_token,
    }
  }

  /**
   * Verify OTP for therapist login
   */
  static async verifyTherapistOtp(
    request: OtpVerificationRequest
  ): Promise<OtpVerificationSuccessResponse> {
    const response = await api.post<TherapistLoginResponse>(
      '/therapist/verify-otp',
      request
    )

    // Map API response to OtpVerificationSuccessResponse format
    const user: User = {
      id: response.data.therapist.id,
      email: response.data.therapist.email,
      firstName: response.data.therapist.firstName,
      lastName: response.data.therapist.lastName,
      role: UserRole.THERAPIST,
      avatar: response.data.therapist.profilePhotoUrl,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      hasHippaSigned: response.data.therapist.hasSignedHIPAA,
      // New provider verification fields
      verificationStatus: response.data.therapist.status?.toLowerCase(),
      blockReason: response.data.therapist.blockReason,
      providerWorkWith: response.data.therapist.providerWorkWith,
    }

    return {
      user,
      token: response.data.access_token,
      refreshToken: response.data.refresh_token,
    }
  }

  /**
   * Resend OTP for therapist login
   */
  static async resendTherapistOtp(email: string): Promise<void> {
    await api.post('/therapist/resend-otp', { email })
  }

  /**
   * Therapist-specific logout endpoint
   */
  static async therapistLogout(): Promise<void> {
    await api.post('/therapist/logout')
  }



  /**
   * Agency-specific login endpoint
   * Returns either AuthResponse (if no OTP required) or OtpVerificationResponse (if OTP required)
   */
  static async agencyLogin(
    credentials: LoginCredentials
  ): Promise<AuthResponse | OtpVerificationResponse> {
    const response = await api.post<
      AgencyLoginResponse | OtpVerificationResponse
    >('/agency/login', credentials)

    // Check if OTP verification is required
    if (
      'step' in response.data &&
      response.data.step === 'OTP_VERIFICATION_REQUIRED'
    ) {
      return response.data as OtpVerificationResponse
    }

    // Map API response to AuthResponse format (legacy flow without OTP)
    const loginResponse = response.data as AgencyLoginResponse
    const user: User = {
      id: loginResponse.agency.id,
      email: loginResponse.user?.email ?? loginResponse.agency.email,
      firstName: loginResponse.user?.firstName ?? loginResponse.agency.firstName,
      lastName: loginResponse.user?.lastName ?? loginResponse.agency.lastName,
      role: UserRole.AGENCY,
      avatar: loginResponse.agency.profilePhotoUrl,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      hasHippaSigned: loginResponse.agency.hasSignedHIPAA,
      // Provider verification fields
      verificationStatus: loginResponse.agency.status?.toLowerCase(),
      blockReason: loginResponse.agency.blockReason,
      agencyName: loginResponse.agency.agencyName,
    }

    return {
      user,
      token: loginResponse.access_token,
      refreshToken: loginResponse.refresh_token,
    }
  }

  /**
   * Verify OTP for agency login
   */
  static async verifyAgencyOtp(
    request: OtpVerificationRequest
  ): Promise<OtpVerificationSuccessResponse> {
    const response = await api.post<AgencyLoginResponse>(
      '/agency/verify-otp',
      request
    )

    // Map API response to OtpVerificationSuccessResponse format
    const user: User = {
      id: response.data.agency.id,
      email: response.data.user?.email ?? response.data.agency.email,
      firstName: response.data.user?.firstName ?? response.data.agency.firstName,
      lastName: response.data.user?.lastName ?? response.data.agency.lastName,
      role: UserRole.AGENCY,
      avatar: response.data.agency.profilePhotoUrl,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      hasHippaSigned: response.data.agency.hasSignedHIPAA,
      // Provider verification fields
      verificationStatus: response.data.agency.status?.toLowerCase(),
      blockReason: response.data.agency.blockReason,
      agencyName: response.data.agency.agencyName,
      permissions: response.data.permission ?? [],
    }

    return {
      user,
      token: response.data.access_token,
      refreshToken: response.data.refresh_token,
    }
  }

  /**
   * Resend OTP for agency login
   */
  static async resendAgencyOtp(email: string): Promise<void> {
    await api.post('/agency/resend-otp', { email })
  }

  /**
   * Agency-specific logout endpoint
   */
  static async agencyLogout(): Promise<void> {
    await api.post('/agency/logout')
  }

  /**
   * Patient-specific login endpoint
   * Returns either AuthResponse (if no OTP required) or OtpVerificationResponse (if OTP required)
   */
  static async patientLogin(
    credentials: LoginCredentials
  ): Promise<AuthResponse | OtpVerificationResponse> {
    interface PatientLoginResponse {
      message: string
      access_token: string
      refresh_token: string
      patient: {
        id: string
        firstName: string
        lastName: string
        email: string
        hasSignedConsent?: boolean
      }
    }

    const response = await api.post<
      PatientLoginResponse | OtpVerificationResponse
    >('/patient/login', credentials)

    // Check if OTP verification is required
    if (
      'step' in response.data &&
      response.data.step === 'OTP_VERIFICATION_REQUIRED'
    ) {
      return response.data as OtpVerificationResponse
    }

    // Map API response to AuthResponse format (legacy flow without OTP)
    const loginResponse = response.data as PatientLoginResponse
    const user: User = {
      id: loginResponse.patient.id,
      email: loginResponse.patient.email,
      firstName: loginResponse.patient.firstName,
      lastName: loginResponse.patient.lastName,
      role: UserRole.CLIENT,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      hasSignedConsent: loginResponse.patient.hasSignedConsent,
    }

    return {
      user,
      token: loginResponse.access_token,
      refreshToken: loginResponse.refresh_token,
    }
  }

  /**
   * Verify OTP for patient login
   */
  static async verifyPatientOtp(
    request: OtpVerificationRequest
  ): Promise<OtpVerificationSuccessResponse> {
    interface PatientLoginResponse {
      message: string
      access_token: string
      refresh_token: string
      patient: {
        id: string
        firstName: string
        lastName: string
        email: string
        hasSignedConsent?: boolean
        profilePhotoUrl?: string
      }
    }

    const response = await api.post<PatientLoginResponse>(
      '/patient/verify-otp',
      request
    )

    // Map API response to OtpVerificationSuccessResponse format
    const user: User = {
      id: response.data.patient.id,
      email: response.data.patient.email,
      firstName: response.data.patient.firstName,
      lastName: response.data.patient.lastName,
      role: UserRole.CLIENT,
      profilePhotoUrl: response.data.patient.profilePhotoUrl,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      hasSignedConsent: response.data.patient.hasSignedConsent,
    }

    return {
      user,
      token: response.data.access_token,
      refreshToken: response.data.refresh_token,
    }
  }

  /**
   * Resend OTP for patient login
   */
  static async resendPatientOtp(email: string): Promise<void> {
    await api.post('/patient/resend-otp', { email })
  }
}

export default AuthService
